import React from "react";
import { CloudOff, RefreshCw, Check, AlertTriangle } from "lucide-react";
import { useOffline } from "@/lib/offline/store";
import { retryFailed } from "@/lib/offline/sync";
import { cn } from "@/lib/utils";

// Subtle top-center pill: offline / syncing / stuck (tap to retry) / all-synced.
export function OfflineIndicator() {
  const { online, pending, syncing, stuck } = useOffline();
  const [justSynced, setJustSynced] = React.useState(false);
  const prevPending = React.useRef(pending);

  React.useEffect(() => {
    if (prevPending.current > 0 && pending === 0 && online && !stuck) {
      setJustSynced(true);
      const t = setTimeout(() => setJustSynced(false), 2200);
      prevPending.current = pending;
      return () => clearTimeout(t);
    }
    prevPending.current = pending;
  }, [pending, online, stuck]);

  const show = !online || pending > 0 || syncing || justSynced || stuck;
  if (!show) return null;

  let icon = <CloudOff className="h-3.5 w-3.5" />;
  let text = pending > 0 ? `Offline · ${pending} queued` : "Offline";
  let tone = "bg-slate-800 text-slate-100 ring-1 ring-white/10";
  let onClick;

  if (online && stuck) {
    icon = <AlertTriangle className="h-3.5 w-3.5" />;
    text = `${pending} didn't sync — tap to retry`;
    tone = "bg-rose-600 text-white";
    onClick = () => retryFailed();
  } else if (online && (syncing || pending > 0)) {
    icon = <RefreshCw className="h-3.5 w-3.5 animate-spin" />;
    text = `Syncing${pending ? ` · ${pending}` : ""}`;
    tone = "bg-emerald-600 text-white";
  } else if (online && justSynced) {
    icon = <Check className="h-3.5 w-3.5" />;
    text = "All synced";
    tone = "bg-emerald-600 text-white";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid="offline-indicator"
      className={cn(
        "fixed left-1/2 top-3 z-[100] flex -translate-x-1/2 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-lg backdrop-blur-sm transition-all",
        onClick && "active:scale-95",
        tone
      )}
    >
      {icon} {text}
    </button>
  );
}
